import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventEntity, EventParticipant, EventSelectedDates, EventSelectedDatesDTO, RecurringDate, UserEntity, UserEventLeaveDates, UserEventLeaveDatesDTO } from '@core/entities';
import { UseEvent } from '@core/usecases/useEvent';
import { UseUser } from '@core/usecases/useUser';
import { RRule } from 'rrule';
import { CalendarEventItem, FilterQuery } from '@core/types';
import { ApprovedStatus, ConfirmationDialogType, DeleteConfirmationOptions, EventHiddenStatus, EventType, NotificationType, SectionType, SocketEvents, UpdateTabType } from '@core/enums';
import { ChangeDetectorRef, inject } from '@angular/core';
import { Logger } from '@core/services';
import {
  CloneArray,
  DateTimeUtility,
  EnumToTabsSelectionArray,
  getAlertTypeByApprovedStatusOfEntity,
  GetTeamId,
  GetUserId,
  isMobileView,
  RRuleTranslator,
  scrollToTop,
  updateObjectProperties,
} from '@core/utils';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent, SingleInputCreateComponent } from '@web/_shared/dialogs';
import { HotToastService } from '@ngxpert/hot-toast';
import { DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionService } from '@auth';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { saveAs } from 'file-saver';
import { formatDate } from '@angular/common';
import { instanceToPlain } from 'class-transformer';

interface FilterOptions {
  timeFrame: 'week' | 'month' | 'year' | 'all-time';
  startOfWeek?: 'sunday' | 'monday';
  additionalFilters?: Record<string, any>;
  sortEvents?: boolean;
}

const log = new Logger('Events Composition');

@UntilDestroy()
export class EventsComposition {
  eventId = 0;
  backUrl = '/m/club-wall/events'; // used in mobile event details screen
  eventsHiddenStatus: EventHiddenStatus = EventHiddenStatus.Unhidden;
  currentSelectedEvent: EventEntity;
  withHiddenEvents: EventEntity[] = [];
  events: EventEntity[] = [];
  filteredEvents: EventEntity[] = [];
  fetchClubEventsOnly = false;
  appointments: any[] = [];
  loadings = {
    eventDetails: true,
    eventList: true,
    appointmentList: true,
    downloadingDocument: false,
    calendarEvents: true,
  };
  paginatedEvents: EventEntity[] = null;

  // --- Specific Properties for Calendar View ---
  isCalendarView = false;
  unFilteredEventsForCalendarView: EventEntity[] = [];
  calendarEvents: CalendarEventItem[];
  appliedFilters: any = {
    year: null,
    month: null,
  };
  // --- end Specific Properties for Calendar View ---

  // These below 2 properties `originalRecord` and `updatedRecord` are used to show admin the changes requested by the user
  originalRecord: EventEntity;
  updatedRecord: EventEntity;
  updateSelectionTabs: { label: string; displayName: string }[] = [];
  currentUpdatedRecordTab = UpdateTabType.CURRENT;

  public readonly _useEvent = new UseEvent();
  protected readonly _permissionService = inject(PermissionService);
  protected readonly _translate = inject(TranslateService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _router = inject(Router);
  protected readonly _activateRoute: ActivatedRoute = inject(ActivatedRoute);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _toast = inject(HotToastService);
  protected _recurringEvents: EventEntity[] = [];

  private readonly _useUser = new UseUser();
  private readonly _cdr = inject(ChangeDetectorRef);

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- Getters & Get Methods --------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get isUserOwner(): boolean {
    return +this.currentSelectedEvent?.author === +GetUserId() || +this.currentSelectedEvent?.group?.groupCreator?.id === +GetUserId() || +this.currentSelectedEvent?.group?.createdBy === +GetUserId();
  }

  get isUserAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  get canUserParticipate(): boolean {
    const userExists = this.currentSelectedEvent?.participants?.find((participant) => participant.userId === +GetUserId());
    if (!userExists) {
      return true;
    } else if (userExists && userExists.approvedStatus === ApprovedStatus.UnAccepted) {
      return true;
    } else {
      return false;
    }
  }

  get isUserJoinedCurrentDate(): boolean {
    if (!this.currentSelectedEvent.selectedDates || this.currentSelectedEvent.selectedDates.length === 0) {
      return false;
    }

    const currentUserId = +GetUserId();
    const currentDateObj = new Date(this.currentSelectedEvent.currentDate).setHours(0, 0, 0, 0);

    return this.currentSelectedEvent.selectedDates.some((selectedDate) => {
      const isCurrentUser = +selectedDate.userId === currentUserId;

      if (!isCurrentUser) {
        return false;
      }

      const startDateObj = new Date(selectedDate.startDate).setHours(0, 0, 0, 0);
      const endDateObj = new Date(selectedDate.endDate).setHours(0, 0, 0, 0);

      // First check if current date falls within the selected date range
      const isWithinSelectedRange = currentDateObj >= startDateObj && currentDateObj <= endDateObj;

      if (isWithinSelectedRange) {
        return true;
      }

      // Additional check for recurring dates with date ranges
      if (!this.currentSelectedEvent?.recurrence && this.currentSelectedEvent.recurringDates?.length) {
        // Find the recurring date range that contains the current date
        const matchingRecurringDate = this.currentSelectedEvent.recurringDates.find((recurringDate) => {
          const recurringStartObj = new Date(recurringDate.dateFrom).setHours(0, 0, 0, 0);
          const recurringEndObj = new Date(recurringDate.dateEnd).setHours(0, 0, 0, 0);

          // Check if current date falls within this recurring date range
          return currentDateObj >= recurringStartObj && currentDateObj <= recurringEndObj;
        });

        // If we found a matching recurring date range
        if (matchingRecurringDate) {
          const recurringStartObj = new Date(matchingRecurringDate.dateFrom).setHours(0, 0, 0, 0);
          const recurringEndObj = new Date(matchingRecurringDate.dateEnd).setHours(0, 0, 0, 0);

          // For date ranges, check if the user's selected dates match the entire recurring range
          if (matchingRecurringDate.dateFrom !== matchingRecurringDate.dateEnd) {
            // This is a date range - user should have joined the entire range
            const selectedStartMatches = startDateObj === recurringStartObj;
            const selectedEndMatches = endDateObj === recurringEndObj;
            return selectedStartMatches && selectedEndMatches;
          } else {
            // This is a single date - check if selected date matches
            return startDateObj === recurringStartObj;
          }
        }
      }

      return false;
    });
  }

  // NEW: unique count of users that joined any date (distinct userIds in selectedDates) - modified to honor currentDate when present
  get uniqueJoinedParticipantsCount(): number {
    const event = this.currentSelectedEvent;
    if (!event?.selectedDates?.length) return 0;

    const normalize = (d: string) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };

    if (event.currentDate) {
      const current = normalize(event.currentDate);
      const ids = new Set<number>();
      event.selectedDates.forEach((sd) => {
        const start = normalize(sd.startDate);
        const end = normalize(sd.endDate);
        if (current >= start && current <= end) {
          ids.add(+sd.userId);
        }
      });
      return ids.size;
    }

    return new Set(event.selectedDates.map((sd) => +sd.userId)).size;
  }

  get canUserAccept(): boolean {
    const userExists = this.currentSelectedEvent?.participants?.find((participant) => participant.userId === +GetUserId());
    if (userExists && (userExists.approvedStatus === ApprovedStatus.UnApproved || userExists.approvedStatus === ApprovedStatus.Pending)) {
      return true;
    } else {
      return false;
    }
  }

  get canUserLeaveEvent(): boolean {
    const userExists = this.currentSelectedEvent?.participants?.find((participant) => participant.userId === +GetUserId());
    return userExists && userExists.approvedStatus === ApprovedStatus.Approved && this.isUserJoinedCurrentDate;
  }

  get canUserDeny(): boolean {
    const userExists = this.currentSelectedEvent?.participants?.find((participant) => participant.userId === +GetUserId());
    return userExists && userExists.approvedStatus === ApprovedStatus.UnApproved;
  }

  get canViewWorkShifts(): boolean {
    return this._permissionService.isWorkShiftsAccessAllowed();
  }

  get admin(): EventParticipant {
    let admin = this.currentSelectedEvent?.participants?.find((member) => member.userId === +this.currentSelectedEvent.author);
    if (!admin) {
      admin = { user: this.currentSelectedEvent?.user } as EventParticipant;
    }
    return admin;
  }

  get approvedParticipantsCount(): number {
    return this.currentSelectedEvent?.participants.filter((participant) => participant.approvedStatus === ApprovedStatus.Approved).length || 0;
  }

  get pendingParticipantsCount(): number {
    return this.currentSelectedEvent?.participants.filter((participant) => participant.approvedStatus === ApprovedStatus.UnApproved).length || 0;
  }

  // NEW: Get count of users who left on the current date
  get usersLeftCurrentDateCount(): number {
    if (!this.currentSelectedEvent?.userLeaveDates?.length) return 0;

    return this.currentSelectedEvent.userLeaveDates.filter((userLeaveDate) => this.hasUserLeftCurrentDate(userLeaveDate)).length;
  }

  get rejectedParticipantsCount(): number {
    const rejectedCount = this.currentSelectedEvent.participants.filter((participant) => participant.approvedStatus === ApprovedStatus.Rejected).length || 0;
    const leftUsersCount = this.usersLeftCurrentDateCount;
    return rejectedCount + leftUsersCount;
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.currentSelectedEvent);
  }

  get isAllowedToViewPendingAndCancelledMembers(): boolean {
    return (
      this._permissionService.isAdmin() ||
      this._permissionService.isSecretary() ||
      this._permissionService.isFunctionary() ||
      this._permissionService.isEditor() ||
      this._permissionService.isMemberLightAdmin()
    );
  }

  get currentRecurringDates(): RecurringDate {
    const currentDateObj = new Date(this.currentSelectedEvent?.currentDate).setHours(0, 0, 0, 0);
    const dateFound = this.currentSelectedEvent?.recurringDates?.find((date) => {
      const dateFromObj = new Date(date.dateFrom).setHours(0, 0, 0, 0);
      return currentDateObj === dateFromObj;
    });
    if (dateFound) {
      return dateFound;
    }
    return {
      dateFrom: this.currentSelectedEvent?.recurringDates[0].dateFrom,
      dateEnd: this.currentSelectedEvent?.recurringDates[this.currentSelectedEvent?.recurringDates.length - 1].dateEnd,
      startTime: this.currentSelectedEvent?.recurringDates[0].startTime,
      endTime: this.currentSelectedEvent?.recurringDates[this.currentSelectedEvent?.recurringDates.length - 1].endTime,
    };
  }

  get getRecurrenceInfo() {
    if (!this.currentSelectedEvent?.recurrence) return '';

    const rruleTranslator = new RRuleTranslator(this._translate);
    return rruleTranslator.translateRRule(this.currentSelectedEvent?.recurrence);
  }

  get showUpdatedRecord(): boolean {
    return this.originalRecord?.updatedRecord && !(this.originalRecord?.denyById && this.originalRecord?.denyByUser);
  }

  get isStatusUnapproved(): boolean {
    return this.currentSelectedEvent?.approvedStatus === ApprovedStatus.UnApproved;
  }

  get isNonRecurringSingleDate(): boolean {
    return !this.currentSelectedEvent?.recurrence && this.currentSelectedEvent?.recurringDates?.length === 1;
  }

  get isNonRecurringMultipleDatesInUpdateMode(): boolean {
    return !this.currentSelectedEvent?.recurrence && this.currentSelectedEvent?.recurringDates?.length > 1 && this.currentUpdatedRecordTab === UpdateTabType.UPDATE;
  }

  get isNonRecurringMultipleDates(): boolean {
    return !this.currentSelectedEvent?.recurrence && this.currentSelectedEvent?.recurringDates?.length > 1;
  }

  // NEW: Check if a user left the event on the current date
  hasUserLeftCurrentDate(userLeaveDate: any): boolean {
    if (!userLeaveDate || !this.currentSelectedEvent?.currentDate) return false;

    const currentDateObj = new Date(this.currentSelectedEvent.currentDate).setHours(0, 0, 0, 0);
    const startDate = new Date(userLeaveDate.startDate).setHours(0, 0, 0, 0);
    const endDate = new Date(userLeaveDate.endDate).setHours(0, 0, 0, 0);

    return currentDateObj >= startDate && currentDateObj <= endDate;
  }

  // NEW: generic check for a participant (not only current user) if they joined currentDate via selectedDates
  hasParticipantJoinedCurrentDate(participant: EventParticipant): boolean {
    if (!participant) return false;
    const selectedDates = this.currentSelectedEvent?.selectedDates;
    if (!selectedDates?.length) return false;

    // If no currentDate, just check if participant has any selectedDates entry
    if (!this.currentSelectedEvent?.currentDate) {
      return selectedDates.some((sd) => +sd.userId === +participant.userId);
    }

    const currentDateObj = new Date(this.currentSelectedEvent.currentDate).setHours(0, 0, 0, 0);

    return selectedDates.some((sd) => {
      if (+sd.userId !== +participant.userId) return false;
      const start = new Date(sd.startDate).setHours(0, 0, 0, 0);
      const end = new Date(sd.endDate).setHours(0, 0, 0, 0);
      if (currentDateObj >= start && currentDateObj <= end) return true;

      if (!this.currentSelectedEvent?.recurrence && this.currentSelectedEvent.recurringDates?.length) {
        const matchedRecurring = this.currentSelectedEvent.recurringDates.find((rd) => {
          const rs = new Date(rd.dateFrom).setHours(0, 0, 0, 0);
          const re = new Date(rd.dateEnd).setHours(0, 0, 0, 0);
          return currentDateObj >= rs && currentDateObj <= re;
        });
        if (matchedRecurring) {
          const rs = new Date(matchedRecurring.dateFrom).setHours(0, 0, 0, 0);
          const re = new Date(matchedRecurring.dateEnd).setHours(0, 0, 0, 0);
          if (matchedRecurring.dateFrom !== matchedRecurring.dateEnd) {
            const startMatches = start === rs;
            const endMatches = end === re;
            return startMatches && endMatches;
          } else {
            return start === rs;
          }
        }
      }
      return false;
    });
  }

  getRecurringEventTime(timeType: 'startTime' | 'endTime'): string {
    return this.currentSelectedEvent?.recurringDates?.length ? this.currentSelectedEvent?.recurringDates[0]?.[timeType] : '';
  }

  canUserJoinEvent(event: EventEntity): boolean {
    const participant = event.participants.find((participant) => participant.userId === +GetUserId());
    return !participant;
  }

  canUserAcceptEvent(event: EventEntity): boolean {
    const participant = event.participants.find((participant) => participant.userId === +GetUserId() && participant.approvedStatus === ApprovedStatus.UnApproved);
    return !!participant;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ Action Methods -----------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  onFilterChange(filter: FilterQuery | any): void {
    const eventsAfterAppliedFilters = (events: EventEntity[]) => {
      return events.filter((event: EventEntity) => {
        if (filter.type && filter.type !== EventType.AllEvents && event.type !== filter.type) {
          return false;
        }

        if (filter.year !== null) {
          const eventYear = new Date(event.currentDate).getFullYear();
          if (eventYear !== filter.year) {
            return false;
          }
        }

        if (filter.month !== 0) {
          const eventMonth = new Date(event.currentDate).getMonth();
          // Adjusting for 1-indexed month in filter (e.g., 1 for January) vs 0-indexed in Date
          if (eventMonth !== filter.month - 1) {
            return false;
          }
        }

        return true;
      });
    };

    this.appliedFilters['year'] = (filter.year as number) || null;
    this.appliedFilters['month'] = (filter.month as number) || null;

    this.filteredEvents = null;
    this.paginatedEvents = null;
    this.filteredEvents = eventsAfterAppliedFilters(this._recurringEvents);

    this.calendarEvents = null;
    this.calendarEvents = this._transformEventsToCalendarEvents(eventsAfterAppliedFilters(this.unFilteredEventsForCalendarView));

    this.loadings.eventList = false;
  }

  onUpdateRecordTabSelection(tabName: UpdateTabType | string) {
    switch (tabName) {
      case UpdateTabType.CURRENT:
        this.currentSelectedEvent = this.originalRecord;
        this.currentUpdatedRecordTab = UpdateTabType.CURRENT;
        break;
      case UpdateTabType.UPDATE:
        this.currentSelectedEvent = this.updatedRecord;
        this.currentUpdatedRecordTab = UpdateTabType.UPDATE;
        break;
      default:
        break;
    }
  }

  async downloadDocument(event: EventEntity) {
    if (event.document) {
      this.loadings.downloadingDocument = true;
      try {
        const filePath = event.document;
        const fileName = filePath.split('/').pop();

        const response = await this._useEvent.downloadDocument(event.id, filePath).toPromise();
        const blob = new Blob([response], { type: 'application/octet-stream' });

        // Use FileSaver to save the file
        saveAs(blob, fileName || 'event-document');
        this.loadings.downloadingDocument = false;
      } catch (error) {
        log.error('Error downloading the document:', error);
        this._toast.error(this._translate.instant('Something went wrong'));
        this.loadings.downloadingDocument = false;
      }
    } else {
      log.error('Event document URL or title is missing.');
      this.loadings.downloadingDocument = false;
    }
  }

  updatePaginatedEvents(events: EventEntity[]) {
    this.paginatedEvents = events as EventEntity[];

    this._cdr.detectChanges();

    setTimeout(() => {
      this.loadings.eventList = false;
      if (isMobileView()) {
        scrollToTop();
      } else {
        scrollToTop('.club-events .card');
      }
    }, 1000);
  }

  deleteEvent(event: EventEntity): void {
    if (!event.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: event.name,
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this._useEvent
                  .deleteEvent(event.id)
                  .pipe(untilDestroyed(this))
                  .subscribe({
                    next: () => {
                      this._toast.success(this._translate.instant('Delete Successful'));
                      this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                      this._reloadCurrentRoute(this.backUrl);
                    },
                  });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: event.name,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this._useEvent
              .deleteEvent(event.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toast.success(this._translate.instant('Delete Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                  this._dialogService.closeAll();
                  // this._reloadCurrentRoute();
                },
              });
          }
        },
      });
    }
  }

  joinEventOnSingleDate(event: EventEntity, btnRef?: ButtonComponent) {
    btnRef?.showLoading();

    // Determine the actual date range for the current event
    let startDate = event.currentDate;
    let endDate = event.currentDate;
    let dateDisplayText = formatDate(event.currentDate, 'dd.MM.yyyy', 'en-US');

    // Check if there's a date range available (similar to event card logic)
    if (!event?.recurrence && event.recurringDates?.length) {
      // Find the recurring date range that contains the current date
      const currentDateObj = new Date(event.currentDate).setHours(0, 0, 0, 0);
      const currentRecurringDate = event.recurringDates.find((date) => {
        const dateFromObj = new Date(date.dateFrom).setHours(0, 0, 0, 0);
        const dateEndObj = new Date(date.dateEnd).setHours(0, 0, 0, 0);

        // Check if current date falls within this recurring date range
        return currentDateObj >= dateFromObj && currentDateObj <= dateEndObj;
      });

      if (currentRecurringDate?.dateEnd && currentRecurringDate.dateEnd !== currentRecurringDate.dateFrom) {
        // This is a date range event - user should join the entire range
        startDate = currentRecurringDate.dateFrom;
        endDate = currentRecurringDate.dateEnd;
        dateDisplayText = `${formatDate(startDate, 'dd.MM.yyyy', 'en-US')} - ${formatDate(endDate, 'dd.MM.yyyy', 'en-US')}`;
      }
    }

    const joinMessage = this._translate.instant('Are you sure you want to join {eventName} event on {eventDate}?', {
      eventName: event.name,
      eventDate: dateDisplayText,
    });

    const confirmJoin = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        messageHTML: joinMessage,
      },
    });

    // TODO: send this body to the backend
    const data: EventSelectedDatesDTO = new EventSelectedDatesDTO();
    data.eventDates = [
      {
        startDate: startDate,
        endDate: endDate,
      },
    ];

    const body = instanceToPlain(data);

    confirmJoin.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .joinEvent(event.id, body)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Join Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                // this._reloadCurrentRoute();
              },
              error: (e) => {
                this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
                btnRef?.showError();
              },
            });
        } else {
          btnRef?.showIdle();
        }
      },
      error: (e) => {
        this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
        btnRef?.showError();
      },
    });
  }

  joinAllEventsInSeries(event: EventEntity, btnRef?: ButtonComponent) {
    btnRef?.showLoading();

    const allDates = this._generateAllEventDates(event);

    // First filter out past dates, then filter out already joined dates
    const futureDates = this._filterFutureDates(allDates);

    const filteredDates = this._filterOutAlreadyJoinedDates(event, futureDates);

    if (filteredDates.length === 0) {
      this._toast.info(this._translate.instant('You have already joined all available dates for this event'));
      btnRef?.showIdle();
      return;
    }

    const joinMessage = this._translate.instant('Are you sure you want to join {eventName} for all upcoming events?', {
      eventName: event.name,
    });

    const confirmJoin = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        messageHTML: joinMessage,
      },
    });

    // Create the body for the API with filtered dates only
    const data: EventSelectedDatesDTO = new EventSelectedDatesDTO();
    data.eventDates = filteredDates;

    const body = instanceToPlain(data);

    confirmJoin.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .joinEvent(event.id, body)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Join Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                // this._reloadCurrentRoute();
              },
            });
        } else {
          btnRef?.showIdle();
        }
      },
      error: (e) => {
        this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
        btnRef?.showError();
      },
    });
  }

  joinEventsWithSelectedDates(event: EventEntity, dates: EventSelectedDates[], btnRef?: ButtonComponent) {
    btnRef?.showLoading();

    // Filter out dates that the user has already joined
    const filteredDates = this._filterOutAlreadyJoinedDates(event, dates);

    if (filteredDates.length === 0) {
      this._toast.info(this._translate.instant('You have already joined all selected dates for this event'));
      btnRef?.showIdle();
      return;
    }

    const joinMessage = this._translate.instant('Are you sure you want to join {eventName} event on selected dates?', {
      eventName: event.name,
    });

    const confirmJoin = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        messageHTML: joinMessage,
      },
    });

    // Create the body for the API with filtered dates only
    const data: EventSelectedDatesDTO = new EventSelectedDatesDTO();
    data.eventDates = filteredDates;

    const body = instanceToPlain(data);

    confirmJoin.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .joinEvent(event.id, body)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Join Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                this._bottomSheetService.closeAll();
                // this._reloadCurrentRoute();
              },
              error: (e) => {
                this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
                btnRef?.showError();
              },
            });
        } else {
          btnRef?.showIdle();
        }
      },
      error: (e) => {
        this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
        btnRef?.showError();
      },
    });
  }

  acceptEvent(event: EventEntity, btnRef: ButtonComponent) {
    btnRef.showLoading();
    const confirmAccept = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        type: ConfirmationDialogType.Accept,
        category: SectionType.EVENT,
      },
    });

    return;

    // TODO: we also need to update accept logic to pass the body with selected dates

    confirmAccept.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .joinEvent(event.id, {})
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Accept Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                // this._reloadCurrentRoute();
              },
            });
        }
        btnRef.showIdle();
      },
    });
  }

  denyInvitedEvent(event: EventEntity, btnRef: ButtonComponent) {
    btnRef.showLoading();
    const confirmDeny = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        type: ConfirmationDialogType.Deny,
        category: SectionType.EVENT,
      },
    });

    confirmDeny.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .denyEvent(event.id)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Deny Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                // this._reloadCurrentRoute();
              },
            });
        } else {
          btnRef.showIdle();
        }
      },
      error: (e) => {
        this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
        btnRef.showError();
      },
    });
  }

  leaveEvent(event: EventEntity, btnRef: ButtonComponent) {
    btnRef.showLoading();

    if (!this.isUserJoinedCurrentDate) {
      this._toast.error(this._translate.instant('You have not joined this event date'));
      btnRef.showIdle();
      return;
    }

    // get id of selectedDate for currentDate for current user
    const currentUserId = +GetUserId();
    const currentDateObj = new Date(event.currentDate).setHours(0, 0, 0, 0);

    const userSelectedDate = event.selectedDates?.find((selectedDate) => {
      const isCurrentUser = +selectedDate.userId === currentUserId;

      if (!isCurrentUser) {
        return false;
      }

      const startDateObj = new Date(selectedDate.startDate).setHours(0, 0, 0, 0);
      const endDateObj = new Date(selectedDate.endDate).setHours(0, 0, 0, 0);

      // Check if current date falls within the selected date range
      return currentDateObj >= startDateObj && currentDateObj <= endDateObj;
    });

    const selectedDateId = userSelectedDate?.id;

    if (!selectedDateId) {
      this._toast.error(this._translate.instant('Selected date not found'));
      btnRef.showIdle();
      return;
    }

    const leaveDatesData: UserEventLeaveDates = {
      id: selectedDateId, // Use the selectedDate ID instead of event ID
      startDate: event.currentDate,
    };

    const leaveEventDTO = new UserEventLeaveDatesDTO();
    leaveEventDTO.leaveDates = [leaveDatesData];

    const confirmDeny = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        type: ConfirmationDialogType.LEAVE,
        category: SectionType.EVENT,
      },
    });

    confirmDeny.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useEvent
            .leaveEvent(event.id, leaveEventDTO)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('Leave Successful'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
                this._dialogService.closeAll();
                // this._reloadCurrentRoute();
              },
            });
        } else {
          btnRef.showIdle();
        }
      },
      error: (e) => {
        this._toast.error(e?.error?.message || this._translate.instant('Something went wrong'));
        btnRef.showError();
      },
    });
  }

  unApproveEvent(btnRef: ButtonComponent) {
    btnRef.showLoading();
    const confirmUnApprove = this._dialogService.open(SingleInputCreateComponent, {
      closeButton: false,
      data: {
        title: this._translate.instant('Are you sure you want to leave this ' + NotificationType.Event) + '?',
        label: this._translate.instant('Message'),
        inputType: 'textArea',
      },
    });

    confirmUnApprove.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          console.log('Yet to implement unapprove event');
        }
        btnRef.showIdle();
      },
    });
  }

  approveEvent(btnRef: ButtonComponent, eventUpdate = false) {
    btnRef.showLoading();
    const confirmApprove = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      data: {
        type: ConfirmationDialogType.APPROVE,
        category: SectionType.EVENT,
      },
    });

    const service$ = eventUpdate ? this._useEvent.approveEventUpdate(this.currentSelectedEvent.id) : this._useEvent.approveEvent(this.currentSelectedEvent.id);

    confirmApprove.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          service$.pipe(untilDestroyed(this)).subscribe({
            next: () => {
              this._toast.success(this._translate.instant('Approve Successful'));
              this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
              this._dialogService.closeAll();
            },
          });
        }
        btnRef.showIdle();
      },
    });
  }

  navigateToChat() {
    if (!this.currentSelectedEvent || !this.admin) return;
    if (+this.admin?.user?.id === +GetUserId()) return;
    this._reloadCurrentRoute(isMobileView() ? '/m/messages/chat' : '/messages/chat', { userId: this.admin?.user?.id });
    this._dialogService?.closeAll();
  }

  // Public available for other compositions to use
  processEventsAndReturn(events: EventEntity[], filterPastEvents = false, sortEvent = false, sortWithTime = false): EventEntity[] {
    this._recurringEvents = [];
    this._processEvents(events, filterPastEvents, sortEvent, sortWithTime);
    return this._recurringEvents;
  }

  filterUpcomingEvents(events: EventEntity[], options: FilterOptions): EventEntity[] {
    const { timeFrame, startOfWeek = 'monday', additionalFilters, sortEvents } = options;
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (timeFrame) {
      case 'week':
        // Start of the week
        if (startOfWeek === 'monday') {
          startDate.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday as start of the week
        } else {
          startDate.setDate(now.getDate() - now.getDay()); // Sunday as start of the week
        }
        startDate.setHours(0, 0, 0, 0);
        // End of the week (Saturday if start is Sunday, otherwise Sunday)
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        // Start of the month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // End of the month
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'year':
        // Start of the year
        startDate = new Date(now.getFullYear(), 0, 1);
        // End of the year
        endDate = new Date(now.getFullYear(), 11, 31);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'all-time':
        // No filtering needed
        return events;
      default:
        break;
    }

    // Filter events based on the selected time frame
    let filteredEvents = events.filter((event) => {
      const eventDate = new Date(event.currentDate);
      return eventDate >= startDate && eventDate <= endDate;
    });

    // Apply additional filters if provided
    if (additionalFilters) {
      for (const key in additionalFilters) {
        if (Object.prototype.hasOwnProperty.call(additionalFilters, key)) {
          filteredEvents = filteredEvents.filter((event) => event[key] === additionalFilters[key]);
        }
      }
    }

    // Optionally sort the filtered events
    if (sortEvents) {
      filteredEvents = filteredEvents.sort((a, b) => new Date(a.currentDate).getTime() - new Date(b.currentDate).getTime());
    }

    return filteredEvents;
  }

  fetchAllEvents(): void {
    this.loadings.eventList = true;
    this.filteredEvents = [];
    this.paginatedEvents = null;
    this.events = [];
    this._recurringEvents = [];

    this._useEvent
      .getAllEvents()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (events) => {
          this.events = CloneArray(events, EventEntity);

          if (this.fetchClubEventsOnly) {
            this.events = this.events.filter((event) => event.type === EventType.ClubEvent);
          }

          if (this.events) {
            this._processEvents(this.events, false, true, false, true); // processing events for calendar view
            this._getTeamMembers(+GetTeamId(), false);
          } else {
            this.loadings.calendarEvents = false;
            this.loadings.eventList = false;
          }

          this._markEventsSectionAsRead();
        },
        error: (error) => {
          this.loadings.eventList = false;
          this._toast.error(error?.error?.message ?? this._translate.instant('Something went wrong'));
          log.error('Error fetching events:', error);
        },
      });
  }

  fetchAllHiddenEvents() {
    this.filteredEvents = [];
    this.paginatedEvents = [];
    this._recurringEvents = [];
    this._useEvent
      .getHiddenEventsList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (events) => {
          this.withHiddenEvents = events;

          if (this.fetchClubEventsOnly) {
            this.withHiddenEvents = this.withHiddenEvents.filter((event) => event.type === EventType.ClubEvent);
          }

          this._setHiddenEventStatus();
          if (events.length) {
            this._getTeamMembers(+GetTeamId(), false);
            this._processEvents(this.events, false, true, false, true); // processing events for calendar view
          } else {
            this.loadings.eventList = false;
            this.loadings.calendarEvents = false;
          }
        },
      });
  }

  matchParticipants(eventParticipants: any[], participants: any[]): any[] {
    return eventParticipants
      .map((participant) => {
        const matchedItem = participants.find((member) => member.id === participant.userId);
        if (matchedItem) {
          participant.user = matchedItem;
        }

        if (participant?.user?.deleted) {
          participant.user.firstName = 'Deleted';
          participant.user.lastName = 'User';
          participant.user.memberId = '0';
        }

        return participant;
      })
      .filter((participant) => participant !== undefined);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _reloadCurrentRoute(path?: string, queryParams?: any): void {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl], { queryParams });
    });
  }

  protected _fetchEvent(): void {
    // Update tabs is just required in details page
    this.updateSelectionTabs = EnumToTabsSelectionArray(UpdateTabType, this._translate);

    this._useEvent
      .getAEvent(this.currentSelectedEvent?.id || this.eventId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (event) => {
          let currentDate;
          if (this.currentSelectedEvent) currentDate = Object.create(this.currentSelectedEvent).currentDate;

          if (isMobileView()) {
            const dateQueryParam = this._activateRoute.snapshot.queryParams['date'];
            if (dateQueryParam) currentDate = dateQueryParam;
          }

          this.currentSelectedEvent = event;

          if (currentDate) this.currentSelectedEvent.currentDate = currentDate;

          this.originalRecord = this.currentSelectedEvent;

          if (this.originalRecord.updatedRecord) {
            this.updatedRecord = updateObjectProperties(this.originalRecord, this.originalRecord.updatedRecord);
          }

          if (this.updatedRecord) {
            this.currentUpdatedRecordTab = this._permissionService.isAdmin() ? UpdateTabType.UPDATE : UpdateTabType.CURRENT;

            if ((!this.updatedRecord.recurrence || this.updatedRecord.recurrence === '') && this.updatedRecord.recurringDates && this.updatedRecord.recurringDates?.length) {
              this.updatedRecord.currentDate = this.updatedRecord.recurringDates[0]?.dateFrom;
            }
          }

          this.loadings.eventDetails = false;

          // Forcefully make get team members call async, as it was causing issue when opening details from notification on Web
          setTimeout(() => {
            this._getTeamMembers(+this.currentSelectedEvent.teamId, true);
          }, 1000);
        },
        error: (error) => {
          if (isMobileView()) {
            this._reloadCurrentRoute('/m/club-wall/events');
          } else {
            this._dialogService.closeAll();
          }
        },
      });
  }

  // Enhanced process events method to generate recurring events with sorting and filtering options
  protected _processEvents(events: EventEntity[], filterPastEvents = false, sortEvent = false, sortWithTime = false, processForCalendarView = false): void {
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0); // Set to midnight UTC for date-only comparison

    if (processForCalendarView) {
      this.calendarEvents = [];
      this.unFilteredEventsForCalendarView = [];
    }

    events.forEach((event) => {
      if (event.recurrence && !event.recurrence.includes('null')) {
        const rruleSet = RRule.fromString(event.recurrence);
        const startDates = rruleSet.all();
        startDates.forEach((startDate) => {
          const startDateUtc = new Date(startDate.toISOString());
          startDateUtc.setUTCHours(0, 0, 0, 0); // Use UTC to avoid timezone issues
          if (!filterPastEvents || (filterPastEvents && startDateUtc >= now)) {
            const eventClone = this._cloneAndAdjustEvent(event, startDate);
            if (processForCalendarView) {
              this.unFilteredEventsForCalendarView.push(eventClone as EventEntity);
            } else {
              this._recurringEvents.push(eventClone as EventEntity);
            }
          }
        });
      } else if (event.recurringDates && event.recurringDates.length > 0) {
        event.recurringDates.forEach((recurringDate) => {
          const startDate = new Date(`${recurringDate.dateFrom}T${recurringDate.startTime}Z`);
          const endDate = new Date(`${recurringDate.dateEnd}T${recurringDate.endTime}Z`);

          if (processForCalendarView && recurringDate.dateFrom !== recurringDate.dateEnd) {
            // Handle date range - clone event for each day in the range
            const rangeStartDate = new Date(`${recurringDate.dateFrom}T00:00:00Z`);
            const rangeEndDate = new Date(`${recurringDate.dateEnd}T00:00:00Z`);

            // Iterate through each day in the range
            const currentDate = new Date(rangeStartDate);
            while (currentDate <= rangeEndDate) {
              const currentDateString = currentDate.toISOString().split('T')[0];

              // Create a new recurring date object for this specific day
              const dayRecurringDate = {
                ...recurringDate,
                dateFrom: currentDateString,
                dateEnd: currentDateString,
              };

              const dayStartDate = new Date(`${currentDateString}T${recurringDate.startTime}Z`);
              const dayEndDate = new Date(`${currentDateString}T${recurringDate.endTime}Z`);

              dayStartDate.setUTCHours(0, 0, 0, 0); // Normalize to UTC

              if (!filterPastEvents || (filterPastEvents && dayEndDate >= now)) {
                const eventClone = this._cloneAndAdjustEventWithTime(event, dayStartDate, dayRecurringDate);
                eventClone.dateTo = this._combineDateTimeUTC(new Date(`${currentDateString}T${recurringDate.endTime}Z`), '');
                this.unFilteredEventsForCalendarView.push(eventClone as EventEntity);
              }

              // Move to next day
              currentDate.setUTCDate(currentDate.getUTCDate() + 1);
            }
          } else {
            // Original logic for single date or non-calendar view
            startDate.setUTCHours(0, 0, 0, 0); // Normalize to UTC
            if (!filterPastEvents || (filterPastEvents && endDate >= now)) {
              const eventClone = this._cloneAndAdjustEventWithTime(event, startDate, recurringDate);
              eventClone.dateTo = this._combineDateTimeUTC(new Date(`${recurringDate.dateFrom}T${recurringDate.endTime}Z`), '');
              if (processForCalendarView) {
                this.unFilteredEventsForCalendarView.push(eventClone as EventEntity);
              } else {
                this._recurringEvents.push(eventClone as EventEntity);
              }
            }
          }
        });
      } else {
        // Handle non-recurring, non-dated events
        const eventStartDateTime = new Date(`${event.dateFrom}Z`);
        eventStartDateTime.setUTCHours(0, 0, 0, 0); // Normalize to UTC
        if (!filterPastEvents || (filterPastEvents && eventStartDateTime >= now)) {
          event.currentDate = event.dateFrom.split('T')[0];
          if (processForCalendarView) {
            this.unFilteredEventsForCalendarView.push(event);
          } else {
            this._recurringEvents.push(event);
          }
        }
      }
    });

    if (sortEvent) {
      if (processForCalendarView) {
        this._sortEvents(this.unFilteredEventsForCalendarView, sortWithTime);
      } else {
        this._sortEvents(this._recurringEvents, sortWithTime);
      }
    }
    if (processForCalendarView) {
      this.calendarEvents = this._transformEventsToCalendarEvents(this.unFilteredEventsForCalendarView);
      this.loadings.calendarEvents = false;
    }
  }

  /**
   * Generates all event dates based on the recurrence rules or recurring dates.
   * Handles RRule strings, recurringDates array, and single date events.
   * Returns an array of objects with startDate and endDate properties.
   * @param event
   * @private
   */
  protected _generateAllEventDates(event: EventEntity): EventSelectedDates[] {
    const allDates: EventSelectedDates[] = [];

    // Case 1: Event has recurrence string (RRule)
    if (event.recurrence && !event.recurrence.includes('null')) {
      try {
        const rruleSet = RRule.fromString(event.recurrence);
        const startDates = rruleSet.all();

        startDates.forEach((startDate) => {
          const dateStr = startDate.toISOString().split('T')[0];
          allDates.push({
            startDate: dateStr,
            endDate: dateStr,
          });
        });
      } catch (error) {
        log.error('Error parsing RRule:', error);
      }
    }
    // Case 2: Event has recurringDates array
    else if (event.recurringDates && event.recurringDates.length > 0) {
      event.recurringDates.forEach((recurringDate) => {
        // Handle date ranges
        if (recurringDate.dateFrom !== recurringDate.dateEnd) {
          const rangeStartDate = new Date(`${recurringDate.dateFrom}T00:00:00Z`);
          const rangeEndDate = new Date(`${recurringDate.dateEnd}T00:00:00Z`);

          // Generate dates for each day in the range
          const currentDate = new Date(rangeStartDate);
          while (currentDate <= rangeEndDate) {
            const dateStr = currentDate.toISOString().split('T')[0];
            allDates.push({
              startDate: dateStr,
              endDate: dateStr,
            });
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
          }
        } else {
          // Single date
          allDates.push({
            startDate: recurringDate.dateFrom,
            endDate: recurringDate.dateEnd || recurringDate.dateFrom,
          });
        }
      });
    }
    // Case 3: Non-recurring event (single date)
    else if (event.dateFrom) {
      const dateStr = event.dateFrom.split('T')[0];
      allDates.push({
        startDate: dateStr,
        endDate: event.dateTo ? event.dateTo.split('T')[0] : dateStr,
      });
    }
    // Case 4: Current date fallback
    else if (event.currentDate) {
      allDates.push({
        startDate: event.currentDate,
        endDate: event.currentDate,
      });
    }

    // Remove duplicates and sort
    const uniqueDates = allDates.reduce((acc, date) => {
      const key = `${date.startDate}-${date.endDate}`;
      if (!acc.find((d) => `${d.startDate}-${d.endDate}` === key)) {
        acc.push(date);
      }
      return acc;
    }, [] as EventSelectedDates[]);

    // Sort by startDate
    uniqueDates.sort((a, b) => a.startDate.localeCompare(b.startDate));

    return uniqueDates;
  }

  /**
   * Filter out dates that are in the past (before today)
   */
  protected _filterFutureDates(dates: EventSelectedDates[]): EventSelectedDates[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    return dates.filter((date) => {
      const eventDate = new Date(date.startDate);
      eventDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      // Only include dates that are today or in the future
      return eventDate >= today;
    });
  }

  private _cloneAndAdjustEvent(event: EventEntity, startDate: Date): Partial<EventEntity> {
    const startTime = event.recurringDates?.[0]?.startTime || '00:00';
    const endTime = event.recurringDates?.[0]?.endTime || '00:00';
    const eventClone: Partial<EventEntity> = { ...event };

    eventClone.dateFrom = this._combineDateTimeUTC(startDate, startTime);
    eventClone.dateTo = this._combineDateTimeUTC(startDate, endTime);

    eventClone.currentDate = startDate.toISOString().split('T')[0]; // This captures the adjusted date in UTC

    eventClone.startTime = startTime;
    eventClone.endTime = endTime;

    return eventClone;
  }

  // New method specifically for recurring dates with specific times
  private _cloneAndAdjustEventWithTime(event: EventEntity, startDate: Date, recurringDate: RecurringDate): Partial<EventEntity> {
    const eventClone: Partial<EventEntity> = { ...event };

    // Use the specific time for this recurring date
    eventClone.dateFrom = this._combineDateTimeUTC(new Date(`${recurringDate.dateFrom}T00:00:00Z`), recurringDate.startTime);
    eventClone.dateTo = this._combineDateTimeUTC(new Date(`${recurringDate.dateEnd}T00:00:00Z`), recurringDate.endTime);

    eventClone.currentDate = recurringDate.dateFrom; // Store the current date as the recurring date

    eventClone.startTime = recurringDate.startTime;
    eventClone.endTime = recurringDate.endTime;

    return eventClone;
  }

  private _sortEvents(events: EventEntity[], sortByTime = false): void {
    events.sort((a, b) => {
      const dateComparison = a.dateFrom.localeCompare(b.dateFrom);
      if (dateComparison !== 0) return dateComparison;

      if (sortByTime) {
        // Assuming startTime is in 'HH:mm' format
        return (a.startTime || '').localeCompare(b.startTime || '');
      }

      return 0;
    });
  }

  private _setHiddenEventStatus() {
    if (!this.withHiddenEvents || !this.withHiddenEvents.length) return;
    this.withHiddenEvents.forEach((hiddenEvent) => {
      const foundEvent = this.events.find((event) => event.id === hiddenEvent.id);
      if (foundEvent) {
        foundEvent.isHidden = false;
      } else {
        hiddenEvent.isHidden = true;
      }
    });
    this.events = [...this.withHiddenEvents];
  }

  private _combineDateTimeUTC(date: Date, time: string): string {
    if (!time || !time.includes(':')) {
      log.warn('Invalid or missing time format. Expected "HH:MM", received:', time);
      time = '00:00';
    }

    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      log.error('Failed to parse hours or minutes from time:', time);
      return null;
    }
    try {
      date.setUTCHours(hours, minutes, 0, 0);
      return date.toISOString();
    } catch (e) {
      log.error('Error setting UTC hours for date:', date, 'with time:', time, e);
      return null;
    }
  }

  private _getTeamMembers(teamId: number, isEventDetails: boolean) {
    this._useEvent
      .getTeamMembers(teamId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (participants) => {
          if (participants && participants.length) {
            if (isEventDetails) {
              this.currentSelectedEvent.participants = this.matchParticipants(this.currentSelectedEvent.participants, participants);
              this.currentSelectedEvent.user = participants.find((user) => user.id === +this.currentSelectedEvent.author);

              if (this.updatedRecord) this.updatedRecord.participants = this._createEventParticipants(this.originalRecord.updatedRecord.participant, participants, this.originalRecord.id);
            } else {
              this.events.forEach((event) => {
                event.participants = this.matchParticipants(event.participants, participants);
              });

              this._processEvents(this.events, true, true);
              this.filteredEvents = this._recurringEvents;

              // Automatically call updatePaginatedEvents to properly set loading state
              if (this.filteredEvents && this.filteredEvents.length > 0) {
                this.updatePaginatedEvents(this.filteredEvents);
              } else {
                // If no events, still need to stop the loading
                this.loadings.eventList = false;
              }
            }
          } else {
            // If no participants, still need to stop the loading
            if (!isEventDetails) {
              this.loadings.eventList = false;
            }
          }
        },
        error: (error) => {
          console.log(error);
          // Make sure to stop loading on error
          if (!isEventDetails) {
            this.loadings.eventList = false;
          }
        },
      });
  }

  private _markEventsSectionAsRead() {
    this._useEvent.markEventsAsRead().pipe(untilDestroyed(this)).subscribe();
  }

  /**
   * Maps an array of user ID and a list of UserEntity objects
   * into an array of EventParticipant entities, all set to Unapproved.
   *
   * @param participantIds - Array of user ID numbers
   * @param users - List of available UserEntity objects
   * @param eventId - The ID of the event these participants belong to
   * @returns Array of EventParticipant instances ready to be persisted
   */
  private _createEventParticipants(participantIds: number[], users: UserEntity[], eventId: number): EventParticipant[] {
    return participantIds.map((idStr) => {
      const userId = Number(idStr);
      const participant = new EventParticipant();

      participant.userId = userId;
      participant.eventId = eventId;

      participant.approvedStatus = ApprovedStatus.UnApproved;
      participant.readStatus = 0;
      participant.eventUpdatedStatus = 0;

      participant.user = users.find((u) => u.id === userId);

      return participant;
    });
  }

  private _transformEventsToCalendarEvents(events: EventEntity[]): CalendarEventItem[] {
    return events.map((event) => {
      const calendarEvent: CalendarEventItem<EventEntity> = {
        id: event.id,
        title: event.name,
        date: event.currentDate,
        startTime: DateTimeUtility.createDateTime(event.currentDate, event.startTime).toISOString(),
        endTime: DateTimeUtility.createDateTime(event.currentDate, event.endTime).toISOString(),
        meta: event,
      };
      return calendarEvent;
    });
  }

  /**
   * Filter out dates that the current user has already joined
   * @param event The event entity containing selectedDates
   * @param datesToFilter Array of dates to filter
   * @returns Array of dates excluding already joined dates
   */
  private _filterOutAlreadyJoinedDates(event: EventEntity, datesToFilter: EventSelectedDates[]): EventSelectedDates[] {
    const currentUserId = +GetUserId();

    if (!event.selectedDates || !currentUserId || !datesToFilter?.length) {
      return datesToFilter || [];
    }

    // Normalize dates to compare only the date part (ignore time and timezone)
    const normalizeDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Get all dates that the current user has already joined (normalized)
    const userJoinedDates = new Set(
      event.selectedDates.filter((selectedDate) => selectedDate.userId === currentUserId).map((selectedDate) => `${normalizeDate(selectedDate.startDate)}-${normalizeDate(selectedDate.endDate)}`),
    );

    // Filter out dates that the user has already joined
    const filteredDates = datesToFilter.filter((date) => {
      const dateKey = `${normalizeDate(date.startDate)}-${normalizeDate(date.endDate)}`;
      const isAlreadyJoined = userJoinedDates.has(dateKey);
      return !isAlreadyJoined;
    });

    return filteredDates;
  }
}
